/**
 * Persistent user settings, stored in localStorage.
 *
 * The companion to {@link module:mission/MissionRecords} — same shape of API and
 * the same defensive posture: a disabled, full, or corrupt store degrades to
 * "no saved settings" (i.e. the SimConfig defaults) rather than throwing.
 *
 * Only the player-facing values from `SETTINGS` are persisted; the rest of that
 * block is static control metadata (slider ranges, option lists) that belongs to
 * the build, not the save. Every stored value is re-validated on read, so a
 * hand-edited or stale blob can't feed a garbage render scale or an unknown
 * cloud-quality preset into the renderer.
 *
 * App holds the authoritative live copy (`App.settings`, seeded from {@link load})
 * and writes each change back through {@link save} as it applies it.
 */

import { SETTINGS } from './SimConfig.js';
import { validJoystickLayout } from '../input/DeviceInput.js';

// Versioned so the schema can change later without colliding with old data.
const STORAGE_KEY = 'flightSim.settings.v1';

// A validator returns the value to use, or undefined to fall back to the default.
const bool = (v) => (typeof v === 'boolean' ? v : undefined);
const oneOf = (options) => (v) => (options.includes(v) ? v : undefined);
const range = (min, max) => (v) => (
  typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : undefined
);

// The persisted key set, and how each value is checked on the way in. Adding a
// setting here is all it takes to make it survive a reload.
const VALIDATORS = {
  hud:              bool,
  mouseSensitivity: range(SETTINGS.mouseSensitivityMin, SETTINGS.mouseSensitivityMax),
  controlScheme:    oneOf(SETTINGS.controlSchemeOptions),
  invertPitch:      bool,
  stickDeadzone:    range(SETTINGS.stickDeadzoneMin, SETTINGS.stickDeadzoneMax),
  // null is a real value here ("use the browser default"), so it has to pass.
  joystickAxes:     (v) => (v === null ? null : validJoystickLayout(v)),
  masterVolume:     range(0, 1),
  musicVolume:      range(0, 1),
  jtacVolume:       range(0, 1),
  voiceVolume:      range(0, 1),
  renderScale:      oneOf(SETTINGS.renderScaleOptions),
  postProcessing:   bool,
  antialiasing:     oneOf(SETTINGS.antialiasingOptions),
  shadowResolution: oneOf(SETTINGS.shadowResolutionOptions),
  cloudQuality:     oneOf(SETTINGS.cloudQualityOptions),
};

/** The settings that persist across sessions, in overlay order. */
export const PERSISTED_KEYS = Object.keys(VALIDATORS);

/** Read the raw stored map. Returns an empty object on any failure. */
function loadRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : {};
  } catch {
    return {};
  }
}

function saveRaw(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage disabled (private mode) or full — persistence is best-effort.
  }
}

/**
 * The full live settings object: the SimConfig defaults with any valid saved
 * values layered over them. Safe to call before anything else exists.
 */
export function load() {
  const stored = loadRaw();
  const settings = { ...SETTINGS };
  for (const [key, validate] of Object.entries(VALIDATORS)) {
    if (!(key in stored)) continue;
    const value = validate(stored[key]);
    if (value !== undefined) settings[key] = value;
  }
  return settings;
}

/**
 * Persist a single setting. Unknown or invalid values are dropped, so this can
 * be called blindly from App's applySetting switch.
 */
export function save(key, value) {
  const validate = VALIDATORS[key];
  if (!validate) return;
  const checked = validate(value);
  if (checked === undefined) return;
  const map = loadRaw();
  map[key] = checked;
  saveRaw(map);
}

/** Wipe all saved settings, returning the game to the SimConfig defaults. */
export function clearAll() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing persisted / storage unavailable — nothing to clear.
  }
}
